import axios from 'axios';
import { sanitizeInput } from '../utils/validation.ts';

const API_BASE_URL = 'https://www.thecocktaildb.com/api/json/v1/1';

export interface Cocktail {
  idDrink: string;
  strDrink: string;
  strDrinkThumb: string;
  strCategory?: string;
  strAlcoholic?: string;
  strGlass?: string;
  strInstructions?: string;
  [key: `strIngredient${number}`]: string | null;
  [key: `strMeasure${number}`]: string | null;
}

export interface FilteredCocktail {
  idDrink: string;
  strDrink: string;
  strDrinkThumb: string;
}

export interface CocktailResponse {
  drinks: Cocktail[] | null;
}

export interface FilteredCocktailResponse {
  drinks: FilteredCocktail[] | null;
}

class CocktailApiService {
  private api = axios.create({
    baseURL: API_BASE_URL,
  });

  // Search cocktails by name
  async searchCocktails(query: string): Promise<Cocktail[]> {
    try {
      const sanitizedQuery = sanitizeInput(query, 100);
      if (!sanitizedQuery) {
        return [];
      }
      const response = await this.api.get<CocktailResponse>(`/search.php?s=${encodeURIComponent(sanitizedQuery)}`);
      return response.data.drinks || [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error searching cocktails:', error);
      }
      return [];
    }
  }

  // Get cocktail by ID
  async getCocktailById(id: string): Promise<Cocktail | null> {
    try {
      // Additional validation - ID should be numeric
      if (!/^\d+$/.test(id) || id.length > 20) {
        return null;
      }
      const response = await this.api.get<CocktailResponse>(`/lookup.php?i=${encodeURIComponent(id)}`);
      return response.data.drinks?.[0] || null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktail:', error);
      }
      return null;
    }
  }

  // Get random cocktail
  async getRandomCocktail(): Promise<Cocktail | null> {
    try {
      const response = await this.api.get<CocktailResponse>('/random.php');
      return response.data.drinks?.[0] || null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching random cocktail:', error);
      }
      return null;
    }
  }

  // Get cocktails by category
  async getCocktailsByCategory(category: string): Promise<Cocktail[]> {
    try {
      const sanitizedCategory = sanitizeInput(category, 100);
      if (!sanitizedCategory) {
        return [];
      }
      const response = await this.api.get<CocktailResponse>(`/filter.php?c=${encodeURIComponent(sanitizedCategory)}`);
      return response.data.drinks || [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktails by category:', error);
      }
      return [];
    }
  }

  // Get all categories
  async getCategories(): Promise<string[]> {
    try {
      const response = await this.api.get<{ drinks: { strCategory: string }[] }>('/list.php?c=list');
      return response.data.drinks.map(cat => cat.strCategory);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching categories:', error);
      }
      return [];
    }
  }

  // Get cocktails by first letter
  async getCocktailsByFirstLetter(letter: string): Promise<Cocktail[]> {
    try {
      // Validate letter is a single uppercase character
      const validatedLetter = letter.trim().toUpperCase();
      if (!/^[A-Z]$/.test(validatedLetter)) {
        return [];
      }
      const response = await this.api.get<CocktailResponse>(`/search.php?f=${encodeURIComponent(validatedLetter)}`);
      return response.data.drinks || [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktails by first letter:', error);
      }
      return [];
    }
  }

  // Get cocktails by ingredient
  async getCocktailsByIngredient(ingredient: string): Promise<Cocktail[]> {
    try {
      const sanitizedIngredient = sanitizeInput(ingredient, 100);
      if (!sanitizedIngredient) {
        return [];
      }
      const response = await this.api.get<FilteredCocktailResponse>(`/filter.php?i=${encodeURIComponent(sanitizedIngredient)}`);
      const drinks = response.data.drinks;
      if (!drinks) {
        return [];
      }
      if (!Array.isArray(drinks)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Drinks is not an array:', drinks);
        }
        return [];
      }
      // Convert FilteredCocktail[] to Cocktail[] (with optional fields)
      return drinks as Cocktail[];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktails by ingredient:', error);
      }
      return [];
    }
  }

  // Get cocktails by glass
  async getCocktailsByGlass(glass: string): Promise<Cocktail[]> {
    try {
      const sanitizedGlass = sanitizeInput(glass, 100);
      if (!sanitizedGlass) {
        return [];
      }
      const response = await this.api.get<FilteredCocktailResponse>(`/filter.php?g=${encodeURIComponent(sanitizedGlass)}`);
      const drinks = response.data.drinks;
      if (!drinks) {
        return [];
      }
      if (!Array.isArray(drinks)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Drinks is not an array:', drinks);
        }
        return [];
      }
      return drinks as Cocktail[];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktails by glass:', error);
      }
      return [];
    }
  }

  // Get cocktails by alcoholic type
  async getCocktailsByAlcoholic(alcoholic: string): Promise<Cocktail[]> {
    try {
      const sanitizedAlcoholic = sanitizeInput(alcoholic, 100);
      if (!sanitizedAlcoholic) {
        return [];
      }
      const response = await this.api.get<FilteredCocktailResponse>(`/filter.php?a=${encodeURIComponent(sanitizedAlcoholic)}`);
      const drinks = response.data.drinks;
      if (!drinks) {
        return [];
      }
      if (!Array.isArray(drinks)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Drinks is not an array:', drinks);
        }
        return [];
      }
      return drinks as Cocktail[];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching cocktails by alcoholic type:', error);
      }
      return [];
    }
  }

  // Get all glasses
  async getGlasses(): Promise<string[]> {
    try {
      const response = await this.api.get<{ drinks: { strGlass: string }[] }>('/list.php?g=list');
      return response.data.drinks.map(glass => glass.strGlass);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching glasses:', error);
      }
      return [];
    }
  }

  // Get all alcoholic types
  async getAlcoholicTypes(): Promise<string[]> {
    try {
      const response = await this.api.get<{ drinks: { strAlcoholic: string }[] }>('/list.php?a=list');
      return response.data.drinks.map(type => type.strAlcoholic);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching alcoholic types:', error);
      }
      return [];
    }
  }
}

export const cocktailApi = new CocktailApiService();
